import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as workersApi from '../api/workers.js';
import WorkersRetentionPanel from './WorkersRetentionPanel.jsx';
import WorkersUsagePanel from './WorkersUsagePanel.jsx';
import { loadUsagePages } from './workers-usage.js';
import { useT } from '../i18n.jsx';
import { metricName } from './WorkersWorkspace.jsx';
import './WorkersBillingPanel.css';

const RESOURCE_ROWS = [
  ['worker_runtime', 'Worker Runtime'],
  ['kv', 'KV'],
  ['d1', 'D1'],
  ['r2', 'R2'],
  ['durable_objects', 'Durable Objects'],
  ['queue', 'Queue'],
  ['workflow', 'Workflow'],
];

const RANGE_MS = {
  '1h': 60 * 60 * 1000,
  '6h': 6 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
};

const KNOWN_LIFECYCLE_STATES = new Set([
  'ACTIVE',
  'LOW_BALANCE',
  'SUSPENDING',
  'SUSPENDED_GRACE',
  'RESUMING',
  'PENDING_DELETION',
  'DELETING',
  'DELETED',
]);

const asMoney = (value) =>
  typeof value === 'string' && value.length ? `$${value}` : '—';

const isZeroMoney = (value) =>
  typeof value === 'string' && /^0+(\.0+)?$/.test(value);

// Preserve decimal strings; do not imply per-operation pricing when the rate
// is actually per million operations or per 30 GB-days.
const rateBasis = (entry, t) => {
  if (typeof entry.unitSize !== 'string' || !/^\d+(\.\d+)?$/.test(entry.unitSize) ||
      !/[1-9]/.test(entry.unitSize) || !entry.unit) return t('wk.s.rateUnknown');
  const [whole, fraction] = entry.unitSize.split('.');
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${fraction ? `.${fraction}` : ''} ${entry.unit}`;
};

const asTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return date.toString() !== 'Invalid Date'
    ? date.toLocaleString()
    : String(value);
};

const exactTime = (value) => (value ? String(value) : 'Unknown');

const errorDetails = (error) => ({
  message: error?.message || 'Billing data could not be loaded.',
  referenceId:
    error?.data?.referenceId ||
    error?.data?.requestId ||
    error?.referenceId ||
    null,
});

const rangeQuery = (range, snapshotTime) => {
  if (range === '1h') return { snapshotTime };
  const end = new Date(snapshotTime);
  if (end.toString() === 'Invalid Date') return { snapshotTime };
  const alignedEnd = new Date(Math.floor(end.getTime() / 300000) * 300000);
  return {
    snapshotTime,
    from: new Date(alignedEnd.getTime() - RANGE_MS[range]).toISOString(),
    to: alignedEnd.toISOString(),
  };
};

export async function loadWorkersBilling(workerId, environment, range, signal) {
  const overview = await workersApi.getBillingOverview(workerId, environment, {
    signal,
  });
  const snapshotTime = overview.snapshotTime;
  const [prices, usage, ledger, forecast, risk, lifecycle] = await Promise.all([
    workersApi.getBillingPrices(workerId, environment, { signal }),
    loadUsagePages(
      workersApi,
      workerId,
      environment,
      rangeQuery(range, snapshotTime),
      { signal },
    ),
    workersApi.getBillingLedger(
      workerId,
      environment,
      { snapshotTime },
      { signal },
    ),
    workersApi.getBillingForecast(workerId, environment, { signal }),
    workersApi.getBillingRisk(workerId, environment, { signal }),
    workersApi.getBillingLifecycle(workerId, environment, { signal }),
  ]);
  return { overview, prices, usage, ledger, forecast, risk, lifecycle };
}

const QualityBadge = ({ value, stale }) => (
  <span className={`workers-billing-quality ${stale ? 'is-stale' : ''}`}>
    <span aria-hidden="true">
      {stale ? '!' : value === 'COMPLETE' ? '✓' : '◷'}
    </span>
    {stale ? 'STALE' : value || 'INDETERMINATE'}
  </span>
);

const LoadingState = () => (
  <div className="workers-billing-loading" aria-label="Loading billing data">
    <div className="workers-billing-skeleton workers-billing-skeleton-head" />
    <div className="workers-billing-summary">
      {Array.from({ length: 6 }, (_, index) => (
        <div className="workers-billing-skeleton" key={index} />
      ))}
    </div>
    <div className="workers-billing-skeleton workers-billing-skeleton-chart" />
    {RESOURCE_ROWS.map(([kind]) => (
      <div className="workers-billing-skeleton" key={kind} />
    ))}
  </div>
);

const ErrorState = ({ error, onRetry }) => {
  const detail = errorDetails(error);
  return (
    <div className="workers-billing-error" role="alert">
      <strong>Billing data unavailable</strong>
      <p>{detail.message}</p>
      {detail.referenceId && <code>Reference: {detail.referenceId}</code>}
      <button className="btn workers-billing-action" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
};

const lifecycleCopy = {
  LOW_BALANCE: ['Low balance', 'Spendable funds may be exhausted soon.'],
  SUSPENDING: [
    'Worker suspension in progress',
    'New dispatch is not assumed active while lifecycle changes are running.',
  ],
  SUSPENDED_GRACE: [
    'Worker suspended — data retained',
    'HTTP dispatch and triggers are stopped; retained storage may still accrue cost.',
  ],
  RESUMING: [
    'Restoring Worker',
    'Access remains unavailable until the service confirms every restore gate.',
  ],
  PENDING_DELETION: [
    'Resources scheduled for deletion',
    'Deletion is irreversible. No delete control is exposed here.',
  ],
  DELETING: [
    'Resource deletion in progress',
    'Access is denied while the server completes irreversible deletion.',
  ],
  DELETED: ['Worker resources deleted', 'This environment is not active.'],
};

const LifecycleBanner = ({ envelope }) => {
  const lifecycle = envelope?.data;
  const state = lifecycle?.state;
  const alerts = [
    ...(lifecycle?.blockerCodes || []),
    ...(lifecycle?.residualRiskStatus ? [`Residual risk: ${lifecycle.residualRiskStatus}`] : []),
    ...(lifecycle?.paymentInFlight ? ['Payment in flight'] : []),
    ...(lifecycle?.legalHold ? ['Legal hold'] : []),
    ...(lifecycle?.providerOutage ? ['Provider unavailable'] : []),
    ...(envelope?.dataQuality !== 'COMPLETE' ? ['Lifecycle data incomplete'] : []),
  ];
  if (state === 'ACTIVE' && !alerts.length) return null;
  const known = KNOWN_LIFECYCLE_STATES.has(state);
  const copy = state === 'ACTIVE'
    ? ['Worker active — billing needs attention', 'Review the reported billing status.']
    : known
    ? lifecycleCopy[state]
    : [
        'Lifecycle state unavailable',
        'The environment is treated as non-active until a known state is returned.',
      ];
  return (
    <section
      className={`workers-billing-banner workers-billing-banner-${known ? String(state).toLowerCase() : 'unknown'}${alerts.length ? ' workers-billing-banner-attention' : ''}`}
      role="status"
      aria-label={copy[0]}
    >
      <span className="workers-billing-banner-icon" aria-hidden="true">
        {alerts.length ? '!' : state === 'DELETED' ? '✓' : state === 'RESUMING' ? '↻' : state === 'LOW_BALANCE' ? '!' : '⛔'}
      </span>
      <div>
        <strong>{copy[0]}</strong>
        {state !== 'DELETED' && <p>{copy[1]}</p>}
        {alerts.length > 0 && <p role="alert">{alerts.join(' · ')}</p>}
        {state !== 'DELETED' && lifecycle?.deletionEarliestAt && (
          <p>Deletion: {asTime(lifecycle.deletionEarliestAt)} · UTC {exactTime(lifecycle.deletionEarliestAt)}</p>
        )}
        {lifecycle?.retentionCostUsd !== undefined && !isZeroMoney(lifecycle.retentionCostUsd) && (
          <p>Retention due: {asMoney(lifecycle.retentionCostUsd)}</p>
        )}
        <details className="workers-billing-disclosure">
          <summary>Lifecycle details</summary>
          {state === 'DELETED' && <p>{copy[1]}</p>}
          <div className="workers-billing-banner-meta mono">
          <span>State: {state || 'UNKNOWN'}</span>
          {lifecycle?.reasonCode && <span>Reason: {lifecycle.reasonCode}</span>}
          {lifecycle?.suspendedAt && (
            <span>Suspended: {asTime(lifecycle.suspendedAt)}</span>
          )}
          {state === 'DELETED' && lifecycle?.deletionEarliestAt && (
            <span>
              Deletion: {asTime(lifecycle.deletionEarliestAt)} · UTC{' '}
              {exactTime(lifecycle.deletionEarliestAt)}
            </span>
          )}
          {lifecycle?.deletedAt && <span>Deleted: {asTime(lifecycle.deletedAt)}</span>}
          {isZeroMoney(lifecycle?.retentionCostUsd) && (
            <span>Retention due: {asMoney(lifecycle.retentionCostUsd)}</span>
          )}
          </div>
        </details>
        {state === 'RESUMING' && (
          <ol className="workers-billing-resume">
            {[
              'Payment received',
              'Outstanding usage settled',
              'Safety reserve restored',
              'Triggers restoring',
              'Active',
            ].map((label, index) => {
              const complete = index < (lifecycle.completedSteps || 0);
              const current =
                !complete &&
                lifecycle.nextStep &&
                index === (lifecycle.completedSteps || 0);
              return (
                <li
                  data-state={
                    complete ? 'complete' : current ? 'current' : 'pending'
                  }
                  key={label}
                >
                  <span aria-hidden="true">
                    {complete ? '✓' : current ? '◷' : '○'}
                  </span>
                  {label}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
};

const Summary = ({ t, data }) => {
  const overview = data.overview.data || {};
  const account = data.risk.data?.accountRisk || {};
  const forecast = data.forecast.data || {};
  const items = [
    [t('wk.s.balance'), account.spendableUsd ?? forecast.spendableUsd],
    [t('wk.h.spendToday'), overview.customerAccruedUsd],
    [
      t('wk.h.budgetLeft'),
      overview.budgetRemainingUsd,
      t('wk.h.budgetNote', { limit: asMoney(overview.dailyBudgetUsd) }),
    ],
  ];
  return (
    <section aria-label="Billing amounts" className="workers-billing-amounts">
      <div className="workers-billing-summary workers-billing-primary">
        {items.map(([label, value, note]) => (
          <div key={label}>
            <span className="micro">{label}</span>
            <strong className="mono">{asMoney(value)}</strong>
            {note && <small>{note}</small>}
          </div>
        ))}
      </div>
    </section>
  );
};

const Resources = ({ t, overview }) => {
  // Show a kind when it has resources, or when it carries a real non-zero
  // amount. An unknown amount on a live resource still renders, as '—'.
  const totals = (overview.data?.resourceTotals || []).filter((total) => {
    const amount = total.customerAccruedUsd ?? total.settledUsd;
    return total.resourceCount > 0 || (amount != null && !isZeroMoney(amount));
  });
  const max = Math.max(
    1e-8,
    ...totals.map((total) => Number(total.customerAccruedUsd) || 0),
  );
  if (!totals.length)
    return (
      <section className="workers-billing-subpanel">
        <strong>{t('wk.s.byResource')}</strong>
        <p className="ww-muted">{t('wk.s.noBreakdown')}</p>
      </section>
    );
  return (
    <section className="workers-billing-subpanel">
      <strong>{t('wk.s.byResource')}</strong>
      <div className="workers-billing-resource-bars">
        {totals.map((total) => {
          const label = RESOURCE_ROWS.find(([kind]) => kind === total.kind);
          return (
            <div className="ww-cost-row" key={total.kind}>
              <span>{label ? label[1] : total.kind}</span>
              <progress max={max} value={Number(total.customerAccruedUsd) || 0} />
              <strong className="mono">
                {asMoney(total.customerAccruedUsd)}
              </strong>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const Ledger = ({ t, envelope }) => {
  const entries = envelope.data?.entries || [];
  return (
    <section className="workers-billing-subpanel">
      <div className="row between">
        <strong>{t('wk.s.ledger')}</strong>
        <span className="micro">{entries.length}</span>
      </div>
      {entries.length ? (
        <div
          className="workers-billing-ledger"
          role="table"
          aria-label={t('wk.s.ledger')}
        >
          {entries.map((entry) => (
            <div role="row" key={entry.id}>
              <span data-label={t('wk.s.col.time')}>
                {asTime(entry.createdAt)}
              </span>
              <strong data-label={t('wk.s.col.item')}>
                {entry.resourceKey || 'Worker'}
              </strong>
              <span data-label={t('wk.r.metric')}>
                {metricName(t, entry.metric)}
              </span>
              <span data-label={t('wk.s.col.qty')} className="mono">
                {entry.quantity} {entry.unit}
              </span>
              <span data-label={t('wk.s.col.price')} className="mono">
                {asMoney(entry.unitPriceUsd)} / {rateBasis(entry, t)}
              </span>
              <span data-label={t('wk.s.col.amount')} className="mono">
                {asMoney(entry.amountUsd)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="workers-billing-empty">{t('wk.s.noLedger')}</div>
      )}
    </section>
  );
};

export const WorkersBillingPanel = ({
  worker,
  initialEnvironment = 'production',
  onEnvironmentChange,
}) => {
  const { t } = useT();
  const [environment, setLocalEnvironment] = useState(initialEnvironment);
  const setEnvironment = (value) => {
    setLocalEnvironment(value);
    setData(null);
    setError(null);
    setLoading(true);
    setRefreshing(false);
    onEnvironmentChange?.(value);
  };
  const [range, setRange] = useState('1h');
  const [snapshot, setData] = useState(null);
  const scope = JSON.stringify([worker.id, environment, range]);
  const data = snapshot?.scope === scope ? snapshot.value : null;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const generation = useRef(0);

  const refresh = useCallback(() => setReloadKey((value) => value + 1), []);

  useEffect(() => {
    const currentGeneration = generation.current + 1;
    generation.current = currentGeneration;
    const controller = new AbortController();
    setError(null);
    if (data) setRefreshing(true);
    else setLoading(true);
    loadWorkersBilling(worker.id, environment, range, controller.signal)
      .then((next) => {
        if (
          generation.current !== currentGeneration ||
          controller.signal.aborted
        )
          return;
        setData({ scope, value: next });
        setError(null);
      })
      .catch((nextError) => {
        if (
          generation.current !== currentGeneration ||
          controller.signal.aborted ||
          nextError?.name === 'AbortError'
        )
          return;
        setError(nextError);
      })
      .finally(() => {
        if (
          generation.current !== currentGeneration ||
          controller.signal.aborted
        )
          return;
        setLoading(false);
        setRefreshing(false);
      });
    return () => controller.abort();
    // data is deliberately last-good state, not a request dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worker.id, environment, range, reloadKey]);

  const qualities = [data?.overview?.dataQuality, data?.usage?.dataQuality,
    ...(data?.usage?.data?.totals || []).map((row) => row.coverage?.status)];
  const quality = qualities.some((value) => !['COMPLETE', 'PARTIAL'].includes(value))
    ? 'INDETERMINATE' : qualities.includes('PARTIAL') ? 'PARTIAL' : 'COMPLETE';
  const delayed =
    data && (quality !== 'COMPLETE' || data.usage.dataQuality !== 'COMPLETE');
  return (
    <section className="card workers-billing" aria-busy={loading || refreshing}>
      <header className="workers-billing-head">
        <div>
          <strong>{t('wk.tab.spend')}</strong>
        </div>
        <div className="workers-billing-controls">
          <label>
            <span className="micro">{t('wk.env')}</span>
            <select
              className="input mono"
              value={environment}
              onChange={(event) => setEnvironment(event.target.value)}
            >
              <option value="preview">PREVIEW</option>
              <option value="production">PRODUCTION</option>
            </select>
          </label>
          <div className="workers-billing-ranges" aria-label="Billing range">
            {Object.keys(RANGE_MS).map((value) => (
              <button
                type="button"
                aria-pressed={range === value}
                onClick={() => setRange(value)}
                key={value}
              >
                {value}
              </button>
            ))}
          </div>
          <button
            className="btn workers-billing-action"
            disabled={refreshing || loading}
            onClick={refresh}
          >
            {refreshing ? t('wk.refreshing') : t('wk.refresh')}
          </button>
        </div>
      </header>
      <div className="sr-only" aria-live="polite">
        {!loading && data
          ? `${environment} billing loaded`
          : ''}
      </div>
      {loading && !data ? (
        <LoadingState />
      ) : error && !data ? (
        <ErrorState error={error} onRetry={refresh} />
      ) : data ? (
        <div className="workers-billing-body">
          <div className="workers-billing-freshness">
            <span title={exactTime(data.overview.snapshotTime)}>
              Updated {asTime(data.overview.snapshotTime)}
            </span>
            <QualityBadge value={quality} stale={Boolean(error)} />
          </div>
          {error && (
            <div className="workers-billing-stale" role="alert">
              <strong>Error — stale data shown.</strong> Last successful
              snapshot {asTime(data.overview.snapshotTime)}.{' '}
              {errorDetails(error).message}
              <button className="btn workers-billing-action" onClick={refresh}>
                Retry
              </button>
            </div>
          )}
          {!error && delayed && (
            <div className="workers-billing-delayed" role="status">
              Billing coverage incomplete. Known amounts remain visible;
              missing intervals stay GAP.
            </div>
          )}
          <LifecycleBanner envelope={data.lifecycle} />
          <WorkersRetentionPanel
            key={`${worker.id}:${environment}`}
            workerId={worker.id}
            environment={environment}
          />
          <Summary t={t} data={data} />
          <WorkersUsagePanel usage={data.usage} t={t} />
          <Resources t={t} overview={data.overview} />
          <Ledger t={t} envelope={data.ledger} />
        </div>
      ) : null}
    </section>
  );
};

export default WorkersBillingPanel;
